import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Check } from "lucide-react";
import { BRAND } from "../data/content";
import { renderBrandPhrases } from "../components/site/BrandLabel";
import { Seo } from "../components/Seo";

const Bullet = ({ children }) => (
  <li className="flex items-start gap-3">
    <span className="mt-0.5 flex h-5 w-5 flex-none items-center justify-center rounded-full bg-food-bg">
      <Check className="h-3 w-3 text-food-accent" strokeWidth={2.5} />
    </span>
    <span className="text-[15px] leading-relaxed text-slate md:text-base">{children}</span>
  </li>
);

const H2 = ({ children }) => (
  <h2 className="text-xl font-bold tracking-tight text-ink md:text-2xl">{children}</h2>
);

const Label = ({ children }) => (
  <span className="font-semibold text-ink">{children}</span>
);

export default function TermsOfService() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="App relative min-h-screen bg-page" data-testid="terms-page">
      <Seo
        title="Terms of Service — WhatThe? Scan"
        description="Terms of Service for the WhatThe? Scan iPhone app: one-time purchase, no subscriptions, no accounts and no data collection."
        path="/terms"
      />
      <header className="sticky top-0 z-50 border-b border-line bg-page/85 backdrop-blur-xl">
        <div className="mx-auto flex max-w-[1240px] items-center justify-between gap-4 px-5 py-3.5 md:px-8">
          <Link to="/" data-testid="terms-nav-logo" className="flex items-center gap-2.5">
            <img src={BRAND.logo} alt="WhatThe?" className="h-9 w-9 rounded-xl" />
            <span className="text-lg font-bold tracking-tight text-ink">
              WhatThe<span className="text-food-accent">?</span> Scan
            </span>
          </Link>
          <Link
            to="/"
            data-testid="terms-back-home"
            className="inline-flex items-center gap-1.5 text-sm font-medium text-slate transition-colors hover:text-ink"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.9} />
            Back to home
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-3xl px-5 pb-24 pt-14 md:px-8 md:pt-20">
        <div className="mb-5 inline-flex items-center gap-2 rounded-full border border-food-accent/25 bg-food-bg px-3.5 py-1.5 text-xs font-semibold text-food-text">
          <ShieldCheck className="h-3.5 w-3.5 text-food-accent" strokeWidth={2} />
          Legal
        </div>

        <h1 className="text-4xl font-bold leading-[1.05] tracking-tight text-ink sm:text-5xl">
          Terms of Service
        </h1>
        <p className="mt-3 text-sm text-slate">Last updated: September 2026</p>

        <p className="mt-6 text-base leading-relaxed text-slate md:text-lg">
          Please read these Terms of Service ("Terms") carefully before using the{" "}
          <Label>{renderBrandPhrases("WhatThe? Scan")} mobile application ("App", "Service")</Label>. By
          downloading, accessing, or using the App, you agree to be bound by these Terms. If you do
          not agree, do not use the App.
        </p>

        <div className="mt-12 space-y-12">
          <section data-testid="terms-section-disclaimer">
            <H2>1. Important Health &amp; Informational Disclaimer</H2>
            <ul className="mt-4 space-y-2.5">
              <Bullet>
                <Label>Informational &amp; Educational Use Only:</Label>{" "}
                {renderBrandPhrases("WhatThe? Scan")} provides automated label interpretation,
                nutritional breakdowns, and ingredient analysis powered by artificial intelligence
                and optical character recognition (OCR). It is designed strictly for informational,
                educational, and general reference purposes.
              </Bullet>
              <Bullet>
                <Label>No Medical, Dietary, or Healthcare Advice:</Label> The App does not provide
                medical, dermatological, nutritional, or clinical healthcare advice, diagnosis, or
                treatment. It is not a medical device. Always seek the advice of a qualified
                physician, accredited practising dietitian, dermatologist, or other licensed
                healthcare professional regarding any medical condition, food intolerance, or severe
                allergy.
              </Bullet>
              <Bullet>
                <Label>Packaging Verification is Mandatory:</Label> AI models can misread text, miss
                obscure additives, or produce inaccurate results.{" "}
                <Label>
                  Never rely solely on this App to verify life-threatening allergens, coeliac safety,
                  or drug interactions.
                </Label>{" "}
                You must always physically inspect the product packaging, allergen declarations, and
                manufacturer ingredient lists prior to consuming or applying any product.
              </Bullet>
              <Bullet>
                <Label>Plate Scanner &amp; Nutritional Estimates:</Label> Any nutritional values,
                macronutrient counts, or calorie estimates provided by meal or plate-scanning
                features are approximate estimates based on standard preparation models. They cannot
                detect hidden seasonings, oils, cooking methods, portion density, or unseen recipe
                variations.
              </Bullet>
            </ul>
          </section>

          <section data-testid="terms-section-eligibility">
            <H2>2. Eligibility and Use of Service</H2>
            <ul className="mt-4 space-y-2.5">
              <Bullet>
                You must be at least 16 years of age (or the minimum legal age in your jurisdiction)
                to use the App.
              </Bullet>
              <Bullet>
                You agree to use the App solely for lawful, personal, and non-commercial consumer
                purposes.
              </Bullet>
              <Bullet>
                You agree not to reverse-engineer, decompile, scrape, copy, or exploit any part of
                the App's proprietary scanning logic, database entries, or code.
              </Bullet>
            </ul>
          </section>

          <section data-testid="terms-section-purchases">
            <H2>3. In-App Purchases (Apple App Store)</H2>
            <div className="mt-4 space-y-4">
              <p className="text-[15px] leading-relaxed text-slate md:text-base">
                <Label>One-Time Purchase:</Label> {renderBrandPhrases("WhatThe? Scan")} features are
                available via a one-time purchase through the Apple App Store. There are no recurring
                subscription charges.
              </p>
              <p className="text-[15px] leading-relaxed text-slate md:text-base">
                <Label>Payment:</Label> Payment is charged to your Apple ID account at confirmation of
                purchase.
              </p>
              <p className="text-[15px] leading-relaxed text-slate md:text-base">
                <Label>Scan Credits:</Label> Purchased scan allocations are non-expiring. Additional
                scan credit top-ups are available as separate one-time purchases.
              </p>
              <p className="text-[15px] leading-relaxed text-slate md:text-base">
                <Label>Refunds:</Label> Refund requests are administered directly through Apple in
                accordance with Apple Media Services Terms and Conditions.
              </p>
            </div>
          </section>
        </div>

        <div className="mt-16 border-t border-line pt-8">
          <Link
            to="/"
            data-testid="terms-footer-back"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-ink transition-colors hover:text-food-accent"
          >
            <ArrowLeft className="h-4 w-4" strokeWidth={1.9} />
            Back to home
          </Link>
          <div className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm font-medium">
            <Link
              to="/privacy-policy"
              data-testid="terms-crosslink-privacy"
              className="text-slate transition-colors hover:text-food-accent"
            >
              Privacy Policy
            </Link>
            <span className="text-line">·</span>
            <Link
              to="/terms"
              data-testid="terms-crosslink-terms"
              className="text-slate transition-colors hover:text-food-accent"
            >
              Terms of Service
            </Link>
          </div>
          <p className="mt-6 text-xs text-slate">
            © {new Date().getFullYear()} WhatThe? Ingredient Scanner · Made in Australia
          </p>
        </div>
      </main>
    </div>
  );
}
